import { defaults } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';

export class PromiseCancelledError extends Error {}

export function makeCancelable(promise) {
  let hasCanceled = false;

  const wrappedPromise = new Promise((resolve, reject) => {
    promise.then(
      (val) =>
        hasCanceled ? reject(new PromiseCancelledError()) : resolve(val),
      (error) =>
        hasCanceled ? reject(new PromiseCancelledError()) : reject(error)
    );
  });

  return {
    promise: wrappedPromise,
    cancel() {
      hasCanceled = true;
    },
  };
}

const DEFAULT_OPTIONS = {
  initiallyLoading: false,
  clearDataOnReload: true,
};

export function useAsyncAction(action, opts = {}) {
  const options = defaults(opts, DEFAULT_OPTIONS);

  const [state, setState] = useState({
    data: undefined,
    error: undefined,
    loading: options.initiallyLoading,
  });

  const abortControllerRef = useRef();

  const actionRef = useRef(action);
  actionRef.current = action;

  const optionsRef = useRef(options);
  optionsRef.current = options;

  const cancelActionRef = useRef();

  const requestIdRef = useRef(0);

  const perform = useCallback(async (...args) => {
    const handler = async () => {
      requestIdRef.current += 1;
      const myRequest = requestIdRef.current;
      try {
        setState((current) => ({
          loading: true,
          error: undefined,
          data: optionsRef.current.clearDataOnReload ? undefined : current.data,
        }));
        const abortCtrl = new AbortController();
        abortControllerRef.current = abortCtrl;
        const data = await actionRef.current(abortCtrl, ...args);

        if (myRequest === requestIdRef.current) {
          setState({ data, loading: false, error: undefined });
        }

        return data;
      } catch (error) {
        if (myRequest === requestIdRef.current) {
          setState({ data: undefined, loading: false, error });
        }

        throw error;
      }
    };

    const { promise, cancel } = makeCancelable(handler());

    cancelActionRef.current = cancel;

    return promise;
  }, []);

  const trigger = useCallback(
    (...args) => {
      perform(...args).catch(() => {});
    },
    [perform]
  );

  const cancel = useCallback(() => cancelActionRef.current?.(), []);

  useEffect(
    () => () => {
      abortControllerRef.current?.abort();
      cancel();
    },
    [cancel]
  );

  return {
    ...state,
    perform,
    trigger,
    cancel,
  };
}
